import { gettext } from '@eventbrite/i18n';
import { getCurrencySymbol } from '@eventbrite/intl';
import classNames from 'classnames';
import { differenceInMinutes, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';

import { getDateFnsLocale } from '@eventbrite/datetime-fns';
import { Typography } from '@eventbrite/marmalade';
import type {
    TimeSlotProps,
    TimeSlotSignalProps,
} from '@eventbrite/timed-entry';
import React, { type ReactElement } from 'react';
import {
    Discount,
    DiscountType,
    EventDate,
    SalesStatus,
    TimeSlot,
    UrgencySignal,
} from '../../../state/infiniteCalendarSeries';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import { getPriceWithCurrencySymbol } from '../../InfiniteCalendarSeries/utils';
import { BoltSvg } from '../assests/BoltSvg';
import { FlameSvg } from '../assests/FlameSvg';
import { PopularRebrandSvg } from '../assests/PopularRebrandSvg';
import { SignalDiscountRebrandSvg } from '../assests/SignalDiscountRebrandSvg';
import { SignalDiscountSvg } from '../assests/SignalDiscountSvg';
import { hasValidDiscount } from '../CalendarContainer';
import {
    DayPart,
    GetUrgencySignalsProps,
    SessionsGroupedByTimeOfDay,
    TAB_HEADERS,
} from '../types';
import { MinPriceDisplay } from './MinPriceDisplay/MinPriceDisplay';
import defaultStyles from './TimeSlot.module.scss';
import rebrandStyles from './TimeSlot.rebranding.module.scss';

type SignalConfig = {
    condition: boolean;
    icon: ReactElement;
    text: string;
};

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

const _getStylesModule = (isAppRebrandActive: boolean) => {
    const theme = getStyles(isAppRebrandActive);
    return stylesModules[theme];
};

export function getSessionDayPart(
    startDatetime: string,
    timezone: string,
): keyof DayPart {
    if (!isValid(parseISO(startDatetime))) {
        throw new Error(
            `Invalid start datetime "${startDatetime}" passed to getSessionDayPart`,
        );
    }
    const startTime = utcToZonedTime(startDatetime, timezone);
    const startHour = startTime.getHours();
    if (startHour >= 4 && startHour < 12) return 'Morning';
    if (startHour >= 12 && startHour < 18) return 'Afternoon';
    return 'Night';
}

function isSlotSoldOut(salesStatus: SalesStatus) {
    return salesStatus === SalesStatus.soldOut;
}

function isSlotDisabled(salesStatus: SalesStatus) {
    return [SalesStatus.soldOut, SalesStatus.notOnSale].includes(salesStatus);
}

function getTimeSlotClassName(
    salesStatus: SalesStatus,
    isAppRebrandActive: boolean,
    showMinPrice: boolean,
) {
    const styles = _getStylesModule(isAppRebrandActive);
    const isSoldOut = isSlotSoldOut(salesStatus);
    const isDisabled = isSlotDisabled(salesStatus);
    return classNames(styles.slot, {
        [styles.soldOutSlot]: isSoldOut,
        [styles.disabledSlot]: isDisabled && !isSoldOut,
        [styles.minPrice]: showMinPrice,
    });
}

function getSessionTextClassName(
    salesStatus: SalesStatus,
    isAppRebrandActive: boolean,
) {
    const styles = _getStylesModule(isAppRebrandActive);
    return isSlotDisabled(salesStatus)
        ? styles.disabledSessionText
        : styles.sessionText;
}

function getSignalContainerClassName(
    isAppRebrandActive: boolean,
    showMinPriceSignal: boolean,
) {
    const styles = _getStylesModule(isAppRebrandActive);
    return showMinPriceSignal ? styles.urgencySignalContainer : '';
}

function getSoldOutSignal(isAppRebrandActive: boolean): TimeSlotSignalProps {
    return {
        className: _getStylesModule(isAppRebrandActive).soldOutSignal,
        content: (
            <Typography
                variant="body-sm-bold"
                as="span"
                className={
                    _getStylesModule(isAppRebrandActive).soldOutSignalText
                }
            >
                {gettext('Sold out').toString()}
            </Typography>
        ),
    };
}

function getMinPrice(
    minPrice: string | undefined,
    currency: string | undefined,
    isAppRebrandActive: boolean,
    salesStatus: SalesStatus,
) {
    if (!minPrice) {
        return null;
    }
    const styles = _getStylesModule(isAppRebrandActive);
    const price = getPriceWithCurrencySymbol(minPrice, currency);
    const priceStyle = isSlotSoldOut(salesStatus)
        ? styles.minPriceContentDisabled
        : styles.minPriceText;
    return {
        className: styles.minPrice,
        content: (
            <MinPriceDisplay
                price={price}
                containerClassName={styles.minPriceContent}
                size="body-md"
                priceClassName={priceStyle}
                fromTextClassName={priceStyle}
            />
        ),
    };
}

function getDiscountSignal(
    discount: Discount | undefined,
    isAppRebrandActive: boolean,
): TimeSlotSignalProps | null {
    const styles = _getStylesModule(isAppRebrandActive);

    if (hasValidDiscount(discount)) {
        return {
            className: styles.discount,
            icon: isAppRebrandActive ? (
                <SignalDiscountRebrandSvg />
            ) : (
                <SignalDiscountSvg />
            ),
        };
    }

    return null;
}

function _getPrimaryUrgencySignal(
    urgencySignals: UrgencySignal,
    isAppRebrandActive: boolean,
): TimeSlotSignalProps | null {
    const styles = _getStylesModule(isAppRebrandActive);
    const signalConfigs: SignalConfig[] = [
        {
            condition: urgencySignals.goingFast,
            icon: <BoltSvg />,
            text: gettext('Going fast').toString(),
        },
        {
            condition: urgencySignals.fewTickets,
            icon: <FlameSvg />,
            text: gettext('Few tickets left').toString(),
        },
        {
            condition: urgencySignals.popular,
            icon: isAppRebrandActive ? <PopularRebrandSvg /> : <FlameSvg />,
            text: gettext('Popular').toString(),
        },
    ];
    const signal = signalConfigs.find((config) => config.condition);

    return signal
        ? {
              className: styles.primaryUrgencySignal,
              icon: signal.icon,
              content: (
                  <Typography
                      variant="body-sm-bold"
                      as="span"
                      className={styles.primaryUrgencySignalText}
                  >
                      {signal.text}
                  </Typography>
              ),
          }
        : null;
}

export function getTimeSlotInfo({
    session,
    isAppRebrandActive,
    showMinPrice,
    currency,
}: GetUrgencySignalsProps) {
    const { urgencySignals, salesStatus, discount, minPrice } = session;

    const isSoldOut = isSlotSoldOut(salesStatus);

    const signals: TimeSlotSignalProps[] = [
        showMinPrice
            ? getMinPrice(minPrice, currency, isAppRebrandActive, salesStatus)
            : null,
        isSoldOut ? getSoldOutSignal(isAppRebrandActive) : null,
        !isSoldOut
            ? _getPrimaryUrgencySignal(urgencySignals, isAppRebrandActive)
            : null,
        !isSoldOut ? getDiscountSignal(discount, isAppRebrandActive) : null,
    ].filter(Boolean) as TimeSlotSignalProps[];

    return signals;
}

export const formatDiscountText = (
    discount: Discount,
    currencySymbol: string,
): string => {
    if (
        discount &&
        discount.type === DiscountType.percentage &&
        discount.percentage > 0
    ) {
        return gettext('%(value)s%(percentage)s off', {
            value: discount.percentage,
            percentage: '%',
        }).toString();
    }
    if (
        discount &&
        discount.type === DiscountType.fixed &&
        discount.amount > 0
    ) {
        return gettext('%(value)s off', {
            value: `${currencySymbol}${discount.amount}`,
        }).toString();
    }
    return '';
};

export function getOverallDiscount<T extends { discount?: Discount }>(
    discountableSessions: Array<T>,
    currency?: string,
) {
    const currencySymbol = getCurrencySymbol(currency);
    const overallDiscount = discountableSessions.find((item) =>
        hasValidDiscount(item.discount),
    );
    if (overallDiscount && overallDiscount.discount) {
        return formatDiscountText(overallDiscount.discount, currencySymbol);
    }

    return undefined;
}

export function getSessionDuration(startUtc: string, endUtc: string) {
    const startDate = parseISO(startUtc);
    const endDate = parseISO(endUtc);
    return differenceInMinutes(endDate, startDate);
}

export function getDurationString(minutes: number) {
    if (minutes < 60) {
        return `${minutes} ${gettext('minutes')}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return remainingMinutes === 0
        ? `${hours} ${hours === 1 ? gettext('hour') : gettext('hours')}`
        : `${(minutes / 60).toFixed(1)} ${gettext('hours')}`;
}

export const getEmptyDayPartMap = <T,>(): Record<keyof DayPart, T[]> => ({
    Morning: [],
    Afternoon: [],
    Night: [],
});

export const getSessionsGroupedByTimeOfDay = (
    timeSlots: readonly TimeSlot[],
    timezone: string,
) =>
    timeSlots.reduce((groups, slot) => {
        const dayPart = getSessionDayPart(slot.startDatetime, timezone);
        groups[dayPart].push(slot);
        return groups;
    }, getEmptyDayPartMap<TimeSlot>());

export const getSessionsInfoText = (
    sessions: TimeSlot[],
    locale: string,
    timezone: string,
) => {
    if (sessions.length <= 1) {
        return '';
    }
    const [firstSession] = sessions;

    if (areAllSessionsDurationEqual(sessions)) {
        const duration = getSessionDuration(
            firstSession.startDatetime,
            firstSession.endDatetime,
        );
        return gettext('Each option is %(duration)s long', {
            duration: getDurationString(duration),
        });
    }

    if (areAllSessionEndTimesEqual(sessions)) {
        const endTime = getFormattedTime(
            firstSession.endDatetime,
            locale,
            timezone,
        );
        return gettext('Entry times vary, all end at %(endTime)s', {
            endTime,
        });
    }

    return '';
};

export const areAllSessionsDurationEqual = (sessions: TimeSlot[]) => {
    if (sessions.length <= 1) {
        return false;
    }
    const durations = sessions.map((session) =>
        getSessionDuration(session.startDatetime, session.endDatetime),
    );
    const firstSessionDuration = durations[0];
    return durations.every((duration) => duration === firstSessionDuration);
};

export const areAllSessionEndTimesEqual = (sessions: TimeSlot[]) => {
    if (sessions.length <= 1) {
        return false;
    }
    const endSessionTimes = sessions.map((session) =>
        Date.parse(session.endDatetime),
    );

    const firstSessionEndTime = endSessionTimes[0];
    return endSessionTimes.every((endTime) => endTime === firstSessionEndTime);
};

const getTimeSlotProps = (
    session: TimeSlot,
    isAppRebrandActive: boolean,
    showEndTime: boolean,
    showMinPrice: boolean,
    currency?: string,
): TimeSlotProps => {
    return {
        id: session.id,
        startDatetime: session.startDatetime,
        endDatetime: showEndTime ? session.endDatetime : undefined,
        urgencySignals: getTimeSlotInfo({
            session,
            isAppRebrandActive,
            showMinPrice,
            currency,
        }),
        isDisable: isSlotDisabled(session.salesStatus),
        className: getTimeSlotClassName(
            session.salesStatus,
            isAppRebrandActive,
            showMinPrice,
        ),
        sessionTextClassName: getSessionTextClassName(
            session.salesStatus,
            isAppRebrandActive,
        ),
        signalContainerClassName: getSignalContainerClassName(
            isAppRebrandActive,
            showMinPrice,
        ),
    };
};

export const mapSessionsToTimeSlotList = (
    sessions: TimeSlot[],
    isAppRebrandActive: boolean,
    currency?: string,
): TimeSlotProps[] => {
    if (!sessions.length) {
        return [];
    }
    const showMinPrice = hasMinPriceVariation(sessions);
    const allSessionsHaveSameDuration = areAllSessionsDurationEqual(sessions);
    const allSessionsHaveSameEndTime = areAllSessionEndTimesEqual(sessions);
    const showEndTime =
        !allSessionsHaveSameDuration && !allSessionsHaveSameEndTime;
    return sessions.map((session) =>
        getTimeSlotProps(
            session,
            isAppRebrandActive,
            showEndTime,
            showMinPrice,
            currency,
        ),
    );
};

export const hasTimeSlots = (
    sessionsByDurationAndTimeOfDay: SessionsGroupedByTimeOfDay,
) =>
    Object.values(sessionsByDurationAndTimeOfDay).some(
        (dayPart) => Object.values(dayPart).length > 0,
    );

export const filterTabHeadersBySessionsInDayPart = (
    sessionsGroupedByTimeOfDay: SessionsGroupedByTimeOfDay,
) =>
    TAB_HEADERS.filter(
        (tab) => sessionsGroupedByTimeOfDay[tab as keyof DayPart].length,
    );

export const getDefaultActiveTab = (
    sessionsGroupedByTimeOfDay: SessionsGroupedByTimeOfDay,
) => {
    for (const tab in sessionsGroupedByTimeOfDay) {
        if (sessionsGroupedByTimeOfDay[tab as keyof DayPart].length) {
            return tab as keyof DayPart;
        }
    }
    return;
};

export const getFormattedTime = (
    isoString: string,
    locale: string,
    timeZone: string,
): string => {
    if (!isoString || isNaN(Date.parse(isoString))) {
        return '';
    }
    return formatInTimeZone(parseISO(isoString), timeZone, 'h:mm aaa', {
        locale: getDateFnsLocale(locale),
    });
};

export function sortSessionsByStartTime<T extends { startDatetime: string }>(
    sessions: T[],
): T[] {
    return sessions.sort(
        (a, b) =>
            new Date(a.startDatetime).getTime() -
            new Date(b.startDatetime).getTime(),
    );
}

export function getTabForSessionId(
    selectedSessionId: string,
    timeSlotData: TimeSlot[],
    timezone: string,
) {
    const session = timeSlotData.find((slot) => slot.id === selectedSessionId);
    if (!session) {
        return;
    }

    return getSessionDayPart(session.startDatetime, timezone);
}

/**
 * Determines if the min price signal should be shown based on all the sessions' min prices.
 * @param sessions - The sessions to check
 * @returns True if there is more than one unique min price among the sessions (i.e., prices are different), false otherwise
 */
export const hasMinPriceVariation = (
    sessions: TimeSlot[] | EventDate[],
): boolean => {
    return (
        sessions.length < 2 ||
        new Set(sessions.map((session) => session.minPrice)).size > 1
    );
};
